import { useState, useEffect, useRef, useCallback } from "react";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { fetchBillingPortalUrl, fetchBranches } from "../api/queries";
import { Fragment } from "react";
import {
  updateStore,
  createBranch,
  updateBranch,
  deleteBranch,
} from "../api/mutations";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { centerCrop, makeAspectCrop } from "react-image-crop";
import BranchList from "../components/BranchList";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { deleteStore } from "../api/mutations";
import DeleteAlert from "../components/DeleteAlert";
import EmptyState from "../components/EmptyState";
import { usePlanAlert } from "../contexts/PlanAlertContext";
import StoreTimeZoneField from "../components/StoreTimeZoneField";
import { currencyOptionsFor } from "../constants/currencies";

export default function Settings() {
  const { t } = useLanguage();
  const { selectedStore } = useStore();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [isStoreSaved, setIsStoreSaved] = useState(false);
  const [response, setResponse] = useState(null);

  // Form states
  const [storeName, setStoreName] = useState(selectedStore?.name || "");
  const [logo, setLogo] = useState(null);
  const [currency, setCurrency] = useState(selectedStore?.currency || "€");
  const [timeZone, setTimeZone] = useState(() =>
    selectedStore?.time_zone || "",
  );
  const [previewUrl, setPreviewUrl] = useState(selectedStore?.logo_url || null);

  // Blob URLs have to be revoked by hand or the object leaks for the page's
  // lifetime. The revoke can't live inside a setPreviewUrl updater (updaters
  // must be pure, and StrictMode double-invokes them) nor in an effect cleanup
  // keyed on previewUrl (StrictMode fires that on its simulated unmount and
  // would kill a preview still on screen). A ref tracks the live blob instead,
  // so the revoke happens exactly once, at the moment it is replaced.
  const previewBlobRef = useRef(null);

  const setPreview = useCallback((next) => {
    if (previewBlobRef.current && previewBlobRef.current !== next) {
      URL.revokeObjectURL(previewBlobRef.current);
    }
    previewBlobRef.current = next?.startsWith("blob:") ? next : null;
    setPreviewUrl(next);
  }, []);

  // Release a preview the user never saved before navigating away.
  useEffect(
    () => () => {
      if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
    },
    [],
  );
  const [errors, setErrors] = useState({ storeName: "", logo: "" });
  const [touched, setTouched] = useState({ storeName: false });
  const [primaryColor, setPrimaryColor] = useState(
    selectedStore?.primary_color || "#3B82F6",
  );
  const [secondaryColor, setSecondaryColor] = useState(
    selectedStore?.secondary_color || "#1E40AF",
  );
  const [backgroundColor, setBackgroundColor] = useState(
    selectedStore?.background_color || "#FFFFFF",
  );

  // The page doesn't remount when the active store changes, so the form state
  // above (seeded once from the initial selectedStore) has to be resynced by hand.
  // Keyed on the id only — a background ["stores"] refetch must not wipe edits.
  useEffect(() => {
    if (!selectedStore?.id) return;

    setStoreName(selectedStore.name || "");
    setCurrency(selectedStore.currency || "€");
    setTimeZone(selectedStore.time_zone || "");
    setPrimaryColor(selectedStore.primary_color || "#3B82F6");
    setSecondaryColor(selectedStore.secondary_color || "#1E40AF");
    setBackgroundColor(selectedStore.background_color || "#FFFFFF");

    setLogo(null);
    setPreview(selectedStore.logo_url || null);

    setErrors({ storeName: "", logo: "" });
    setTouched({ storeName: false });
    setResponse(null);
    setIsStoreSaved(false);
    setIsAdding(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.id]);

  // Image cropping states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [crop, setCrop] = useState();

  const { showPlanAlert } = usePlanAlert();

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", selectedStore?.id],
    queryFn: () => fetchBranches(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const updateStoreMutation = useMutation({
    mutationFn: updateStore,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      setResponse({
        success: true,
        message: t("common.savedChanges"),
      });
    },
    onError: (error) => {
      console.error("Error updating store:", error);
      setResponse({
        success: false,
        message: error.response.data.error,
      });
    },
  });

  const createBranchMutation = useMutation({
    mutationFn: createBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({
        queryKey: ["branches", selectedStore?.id],
      });
    },
  });

  const updateBranchMutation = useMutation({
    mutationFn: updateBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({
        queryKey: ["branches", selectedStore?.id],
      });
    },
  });

  const deleteBranchMutation = useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({
        queryKey: ["branches", selectedStore?.id],
      });
    },
  });

  const deleteStoreMutation = useMutation({
    mutationFn: deleteStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

  // Add effect to hide the success message after 3 seconds
  useEffect(() => {
    let timer;
    if (isStoreSaved) {
      timer = setTimeout(() => {
        setIsStoreSaved(false);
      }, 3000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isStoreSaved]);

  const handleBillingPortal = async () => {
    try {
      const response = await queryClient.fetchQuery({
        queryKey: ["billingPortal", selectedStore.id],
        queryFn: () => fetchBillingPortalUrl(selectedStore.id),
      });

      window.location.href = response.portal_url;
    } catch (error) {
      console.error("Error accessing billing portal:", error);
    }
  };

  const validateStoreName = (name) => {
    if (!name.trim()) {
      return t("settings.storeName.required");
    }
    if (name.trim().length < 2) {
      return t("settings.storeName.minLength");
    }
    if (name.trim().length > 50) {
      return t("settings.storeName.maxLength");
    }
    return "";
  };

  const handleStoreNameChange = (e) => {
    const value = e.target.value;
    setStoreName(value);

    if (touched.storeName) {
      setErrors((prev) => ({
        ...prev,
        storeName: validateStoreName(value),
      }));
    }
  };

  const makeImageSquare = async (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Make canvas square based on larger dimension
        const size = Math.max(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        // Make background transparent
        ctx.clearRect(0, 0, size, size);

        // Center the image
        const xOffset = (size - img.width) / 2;
        const yOffset = (size - img.height) / 2;

        // Draw image centered
        ctx.drawImage(img, xOffset, yOffset, img.width, img.height);

        // Convert to data URL
        canvas.toBlob((blob) => {
          resolve(URL.createObjectURL(blob));
        }, "image/png");
      };
      img.src = imageUrl;
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const squareImageUrl = await makeImageSquare(reader.result);
        setTempImageUrl(squareImageUrl);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = async (image, crop) => {
    const canvas = document.createElement("canvas");
    const pixelRatio = window.devicePixelRatio;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: (crop.x * image.width) / 100,
      y: (crop.y * image.height) / 100,
      width: (crop.width * image.width) / 100,
      height: (crop.height * image.height) / 100,
    };

    canvas.width = pixelCrop.width * pixelRatio;
    canvas.height = pixelCrop.height * pixelRatio;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }
          blob.name = "cropped-logo.png";
          resolve(blob);
        },
        "image/png",
        1,
      );
    });
  };

  const handleCropComplete = async () => {
    if (imageRef && crop.width && crop.height) {
      try {
        const croppedImage = await getCroppedImg(imageRef, crop);
        if (croppedImage) {
          const newPreviewUrl = URL.createObjectURL(croppedImage);
          setLogo(croppedImage);
          setPreview(newPreviewUrl);
          setIsModalOpen(false);
          setTempImageUrl(null);
        }
      } catch (error) {
        console.error("Cropping failed:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nameError = validateStoreName(storeName);
    setErrors({
      storeName: nameError,
      logo: "",
    });

    if (nameError) {
      setTouched({ storeName: true });
      return;
    }

    const updateData = {
      storeId: selectedStore.id,
      name: storeName,
      ...(logo && { logo }),
      currency: currency,
      timeZone: timeZone,
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      backgroundColor: backgroundColor,
    };

    updateStoreMutation.mutate(updateData);
    setIsStoreSaved(true);
  };

  const onImageLoad = (e) => {
    const { width, height } = e.target;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 100,
        },
        1,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(crop);
    setImageRef(e.target);
  };

  const handleAddBranch = (branchData) => {
    const isInTrialPeriod = () => {
      if (!selectedStore?.trial_expires_at) return false;
      const trialEndDate = new Date(selectedStore.trial_expires_at);
      trialEndDate.setDate(trialEndDate.getDate());
      return new Date() < trialEndDate;
    };

    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "past_due" &&
      selectedStore?.subscription_status !== "trialing" &&
      !isInTrialPeriod()
    ) {
      alert(t("settings.branches.trialEnded"));
      return;
    }

    createBranchMutation.mutate({
      storeId: selectedStore.id,
      name: branchData.name,
      address: branchData.address,
      latitude: branchData.latitude,
      longitude: branchData.longitude,
    });
  };

  const handleEditBranch = (branchId, updatedBranch) => {
    updateBranchMutation.mutate({
      storeId: selectedStore.id,
      branchId,
      name: updatedBranch.name,
      address: updatedBranch.address,
      latitude: updatedBranch.latitude,
      longitude: updatedBranch.longitude,
    });
  };

  const handleDeleteBranch = (branchId) => {
    deleteBranchMutation.mutate({
      storeId: selectedStore.id,
      branchId,
    });
  };

  return (
    <div className="">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Store Settings */}
        <div className="bg-white shadow-sm rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              {t("settings.title")}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Store Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("settings.storeName.label")}
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="store-name"
                    value={storeName}
                    onChange={handleStoreNameChange}
                    onBlur={() => setTouched({ storeName: true })}
                    className={`block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.storeName ? "border-red-300" : ""
                    }`}
                  />
                  {errors.storeName && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.storeName}
                    </p>
                  )}
                </div>
              </div>

              {/* Store Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("store.logoLabel")}
                </label>
                <div
                  onClick={() => document.getElementById("logo-upload").click()}
                  className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-300 px-6 py-8 hover:border-gray-400 cursor-pointer"
                >
                  <div className="text-center">
                    {previewUrl ? (
                      <div>
                        <img
                          src={previewUrl}
                          alt="Logo preview"
                          className="mx-auto h-32 w-32 object-contain rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreview(null);
                            setLogo(null);
                          }}
                          className="mt-4 text-sm font-medium text-red-600 hover:text-red-500"
                        >
                          {t("settings.logo.remove")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <span className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                            {t("settings.logo.upload")}
                          </span>
                        </div>
                      </>
                    )}
                    <input
                      id="logo-upload"
                      name="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="sr-only"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("store.currency")}
                </label>
                <div className="mt-1">
                  <select
                    id="currency"
                    name="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.currency ? "border-red-300" : "border-gray-300"
                    }`}
                  >
                    {currencyOptionsFor(currency).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.currency && (
                  <p className="mt-2 text-sm text-red-600">{errors.currency}</p>
                )}
              </div>

              <StoreTimeZoneField
                value={timeZone}
                onChange={setTimeZone}
                savedTimeZone={selectedStore?.time_zone || null}
                t={t}
              />

              {/* Brand Colors */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("store.brandColors")}
                </label>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">
                      {t("store.primaryColor")}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="w-8 h-8"
                      />
                      <input
                        type="text"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">
                      {t("store.secondaryColor")}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="w-8 h-8"
                      />
                      <input
                        type="text"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">
                      {t("store.backgroundColor")}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-8 h-8"
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="flex items-center gap-4">
                  {isStoreSaved && (
                    <div className="rounded-md">
                      <div
                        className={`text-sm ${response?.success ? "text-green-700" : "text-red-700"}`}
                      >
                        {response?.message}
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={updateStoreMutation.isPending}
                    className={`inline-flex items-center px-6 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${updateStoreMutation.isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {updateStoreMutation.isPending
                      ? t("common.saving")
                      : t("common.save")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - Branches */}
        <div className="bg-white shadow-sm rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                {t("settings.branches.title")}
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (selectedStore?.features.branch_limit <= branches.length) {
                    showPlanAlert({
                      title: t("featureLimit.title"),
                      description: t("featureLimit.description", {
                        plan: selectedStore?.current_plan,
                        limit: selectedStore?.features.branch_limit,
                        feature: t("featureLimit.features.branch", {
                          count: selectedStore?.features.branch_limit,
                        }),
                      }),
                    });
                  } else {
                    setIsAdding(true);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t("settings.branches.addButton")}
              </button>
            </div>

            {branches.length === 0 && !isAdding && <EmptyState />}

            <BranchList
              branches={branches}
              onAddBranch={handleAddBranch}
              onEditBranch={handleEditBranch}
              onDeleteBranch={handleDeleteBranch}
              isAdding={isAdding}
              onCancelAdd={() => setIsAdding(false)}
              store={selectedStore}
            />
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg ">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  {t("settings.billing.title")}
                </h2>
                <p
                  className="mt-1 text-sm text-gray-500"
                  dangerouslySetInnerHTML={{
                    __html:
                      selectedStore?.subscription_status === "active" ||
                      selectedStore?.subscription_status === "past_due" ||
                      selectedStore?.subscription_status === "trialing"
                        ? t("settings.billing.descriptionCurrentPlan", {
                            plan: selectedStore?.current_plan,
                          })
                        : t("settings.billing.description"),
                  }}
                />
              </div>
              {selectedStore?.subscription_status === "active" ||
              selectedStore?.subscription_status === "past_due" ||
              selectedStore?.subscription_status === "trialing" ? (
                <button
                  onClick={handleBillingPortal}
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {t("settings.billing.manageSubscription")}
                </button>
              ) : (
                <span className="text-sm text-gray-500">
                  {t("settings.billing.noSubscription")}
                  <br />
                  <Link
                    to="/pricing"
                    className="text-indigo-600 hover:text-indigo-500"
                  >
                    {t("settings.billing.viewPlans")}
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>

        {selectedStore?.subscription_status != "active" &&
        selectedStore?.subscription_status != "past_due" &&
        selectedStore?.subscription_status != "trialing" ? (
          <div className="bg-white shadow-sm rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">
                    {t("settings.delete.title")}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {t("settings.delete.description")}
                  </p>
                </div>

                <button
                  onClick={() => setIsDeleteAlertOpen(true)}
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {t("settings.delete.deleteStore")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Image Cropping Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-60"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4"
                  >
                    {t("settings.logo.crop")}
                  </Dialog.Title>

                  <div className="mt-2">
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      aspect={1}
                      circularCrop={false}
                      minWidth={100}
                      minHeight={100}
                    >
                      <img
                        src={tempImageUrl}
                        onLoad={onImageLoad}
                        alt="Crop me"
                        style={{ maxWidth: "100%" }}
                      />
                    </ReactCrop>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                      onClick={handleCropComplete}
                    >
                      {t("settings.logo.cropButton")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsModalOpen(false)}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteStoreMutation.mutate({ storeId: selectedStore.id });
          setIsDeleteAlertOpen(false);
        }}
        title={"settings.delete.modal.title"}
        description={"settings.delete.modal.description"}
        isValidationRequired={true}
        validationValue={storeName}
        validationPlaceholder={"settings.delete.modal.validationPlaceholder"}
      />
    </div>
  );
}
